import { BaseClient } from '../base-client';
import type {
  CreateSkillInput,
  ExportSkillsQuery,
  ImportSkillsResult,
  ListSkillsQuery,
  PatchSkillInput,
  Skill,
  SkillCategorySummary,
} from './types';

type One = { data: Skill };
type ListResponse = { data: Skill[]; meta: { total: number; limit: number; offset: number } };
type CategoriesResponse = { data: SkillCategorySummary[] };
type ImportResponse = { data: ImportSkillsResult };

export class SkillsClient extends BaseClient {
  list(query: ListSkillsQuery = {}): Promise<ListResponse> {
    return this.request('/api/skills', { query: normalize(query) });
  }

  /** Category/type rollup over active skills — backs the Categories tab and
   *  the master-list category filter dropdown. */
  categories(): Promise<CategoriesResponse> {
    return this.request('/api/skills/categories');
  }

  /** Batch fetch skills by exactly the referenced ids — bounded, never
   *  capped. See `BaseClient.findByIdsAt`. */
  findByIds(ids: readonly string[]): Promise<ListResponse> {
    return this.findByIdsAt<Skill>('/api/skills/find', ids);
  }

  get(id: string): Promise<One> {
    return this.request(`/api/skills/${encodeURIComponent(id)}`);
  }

  create(input: CreateSkillInput): Promise<One> {
    return this.request('/api/skills', { method: 'POST', body: input });
  }

  patch(id: string, input: PatchSkillInput): Promise<One> {
    return this.request(`/api/skills/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  remove(id: string): Promise<One> {
    return this.request(`/api/skills/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: {},
    });
  }

  restore(id: string): Promise<One> {
    return this.request(`/api/skills/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
      body: {},
    });
  }

  /**
   * Download the catalog as an XLSX blob. `filter` mirrors the Master
   * list's filters (search text, status, type, category); pass `{}` to
   * export every skill.
   */
  exportXlsx(filter: ExportSkillsQuery = {}): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/skills/export', { query: normalize(filter) });
  }

  /** Download a blank XLSX template (header row only). */
  exportTemplate(): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/skills/import/template');
  }

  /**
   * Upload an .xlsx or .csv. Server validates every row before
   * persisting any of them; failed rows come back in `result.rows` with
   * per-row error messages for the FE report.
   */
  importFile(file: File): Promise<ImportResponse> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return this.requestMultipart('/api/skills/import', fd);
  }
}

function normalize(q: object): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}
